import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCompletionRateProfile,
  resetCompletionRateProfileState,
} from '@/redux/completionRate/completionRateProfileSlice';
import { Form, Input, Button, Table, Modal, Spin, Tag } from 'antd';
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils';

const defaultPageSize = 20

class CompletionRateForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {},
    };
  }

  componentDidMount() {
    const { dispatch, data, query = {}, serviceType } = this.props;

    // 合并传入的 query 和 member_id
    // 注意：member_id 放在后面，确保不会被 query 中的值覆盖
    const finalQuery = {
      ...query,
      member_id: data.member_id ,
    };

    dispatch(
      fetchCompletionRateProfile({
        serviceType,
        query: finalQuery,
      })
    );
  }

  componentWillUnmount() {
    const { dispatch, serviceType } = this.props;
    // 组件卸载时清空数据
    if (dispatch) {
      dispatch(
        resetCompletionRateProfileState({
          serviceType,
        })
      );
    }
  }

  /**
   * 格式化状态
   * @param {number} status - 状态值
   * @returns {string} 状态文本
   */
  formatStatus = (status) => {
    const statusMap = {
      0: { text: '未完成', color: 'red' },
      1: { text: '完成', color: 'green' },
    };
    const statusInfo = statusMap[status] || { text: '-', color: 'default' };
    return statusInfo;
  };

  /**
   * 格式化通知状态
   * @param {string} alert - 通知状态
   * @returns {Object} 通知状态信息
   */
  formatAlert = (alert) => {
    const alertMap = {
      noNotification: { text: '无需通知', color: 'default' },
      needNotification: { text: '需要通知', color: 'orange' },
      notified: { text: '已通知', color: 'green' },
    };
    const alertInfo = alertMap[alert] || { text: '-', color: 'default' };
    return alertInfo;
  };

  handlePaginationChange = (pageNumber, pageSize) => {
    const { dispatch, serviceType, completionRateProfileObj: { [serviceType]: { query } } } = this.props
    
    dispatch(
      fetchCompletionRateProfile({
        serviceType,
        query: {
          ...query,
          pageNumber,
          pageSize
        },
      })
    )
  }

  render() {
    const { data, serviceType, completionRateProfileObj = {} } = this.props;

    const { query, fetching } = completionRateProfileObj
    // 根据 serviceType 获取对应的数据
    const renderData =
      (serviceType && completionRateProfileObj[serviceType]) ||
      completionRateProfileObj.data || {
        list: [],
        total: 0,
        pageNumber: 1,
        pageSize: 20,
      };

    const columns = [
      {
        title: '状态/通知',
        key: 'status_alert',
        width: '15%',
        render: (record) => {
          const statusInfo = this.formatStatus(record.status);
          const alertInfo = this.formatAlert(record.alert);
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#666' }}></span>{' '}
                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
              </div>
              <div>
                <span style={{ color: '#666' }}></span>{' '}
                <Tag color={alertInfo.color}>{alertInfo.text}</Tag>
              </div>
            </div>
          );
        },
      },
      {
        title: '员工/部门/项目',
        key: 'agent_department_name',
        width: '25%',
        render: (record) => (
          <div>
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: '#666' }}>员工:</span>{' '}
              {renderTextStr(record.agent_name)}
            </div>
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: '#666' }}>部门:</span>{' '}
              {renderTextStr(record.department)}
            </div>
            <div>
              <span style={{ color: '#666' }}>项目:</span>{' '}
              {renderTextStr(record.name)}
            </div>
          </div>
        ),
      },
      {
        title: '异常记录',
        dataIndex: 'exception',
        key: 'exception',
        width: '35%',
        render: (text) => renderTextStr(text),
      },
      {
        title: '时间信息',
        key: 'time_info',
        width: '25%',
        render: (record) => {
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#666' }}>开始:</span>{' '}
                {formatDateTimeStr(record.start_datetime)}
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#666' }}>领取:</span>{' '}
                {formatDateTimeStr(record.receive_time)}
              </div>
              <div>
                <span style={{ color: '#666' }}>截止:</span>{' '}
                {formatDateTimeStr(record.deadline)}
              </div>
            </div>
          );
        },
      },
    ];
    const finalPagination = {
      current: query.pageNumber || 1,
      pageSize: query.pageSize || defaultPageSize,
      total: renderData.total || 0,
      showTotal: total => `共 ${total} 条数据`,
      onChange: this.handlePaginationChange
    }
    return (
      <Table
        loading={fetching}
        columns={columns}
        dataSource={renderData.list || []}
        rowKey={(record, index) => record.order_total_id || index}
        pagination={renderData.total > defaultPageSize ? finalPagination : false}
        scroll={{ x: 752 }}
        size="small"
      />
    );
  }
}

// 使用 HOC 包装组件以注入 Redux
const CompletionRateFormWrapper = (props) => {
  const dispatch = useDispatch();
  const completionRateProfileObj = useSelector(
    (state) => state.completionRate.completionRateProfile || {}
  );

  return (
    <CompletionRateForm
      {...props}
      dispatch={dispatch}
      completionRateProfileObj={completionRateProfileObj}
    />
  );
};

export default CompletionRateFormWrapper;